import Button from '@atlaskit/button/new';
import { Inline, Stack, xcss } from '@atlaskit/primitives';
import { GlobalPageLoadExperience } from '@atlassian/ufo';
import { useAnalytics } from '@databases/analytics';
import { getBaseUrl, useDatabaseContent } from '@databases/database-state';
import { currentBridgeData } from '@databases/database-state/state/bridge-data';
import { defaultStoreUtils } from '@databases/redux';
import {
  type Database,
  type BrokenDatabase,
  type DatabaseField,
  type EntryCreateOptions,
  type EntryWithContext,
} from '@databases/types/database';
import useView from '@databases/views/useView';
import React, {
  type FunctionComponent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';
import { useIntl } from 'react-intl-next';

import { messages } from '../../i18n';
import {
  DatabasePreview,
  type DatabasePreviewComponentProps,
} from '../databasePreview/DatabasePreview';
import EmbedReferencePicker from '../embedReferencePicker/EmbedReferencePicker';
import { getEmbedUrl } from '../embedReferencePicker/embed';

export interface EmbedReferenceProps {
  ToolbarComponent: React.FunctionComponent<{
    contentId: string;
    actions: any;
    canEditFields: boolean;
    canEditContent: boolean;
    showViewSettings?: boolean;
  }>;
  DatabasePreviewComponent: React.FunctionComponent<DatabasePreviewComponentProps>;
  EntryPreviewComponent: React.FunctionComponent<{
    contentId: string;
    entryId: string;
  }>;
  ValuePreviewComponent: React.FunctionComponent<{
    contentId: string;
    entryId: string;
    fieldId: string;
  }>;
}

export const cancelDatabaseReference = () => {
  globalThis.parent.postMessage(
    JSON.stringify({ cancelDatabaseReference: true }),
    globalThis.location.origin
  );
};

const EmbedReference: FunctionComponent<EmbedReferenceProps> = ({
  ToolbarComponent,
  DatabasePreviewComponent,
  EntryPreviewComponent,
  ValuePreviewComponent,
}) => {
  const [selectedDatabase, setSelectedDatabase] = useState<
    Database | BrokenDatabase | undefined
  >(undefined);
  const [selectedEntry, setSelectedEntry] = useState<
    EntryWithContext | EntryCreateOptions | undefined
  >(undefined);
  const [selectedField, setSelectedField] = useState<DatabaseField | undefined>(
    undefined
  );
  const [databaseError, setDatabaseError] = useState(false);
  const [embedLoading, setEmbedLoading] = useState<boolean>(false);
  const [isEmptySubmit, setIsEmptySubmit] = useState<boolean>(false);

  const { spaceKey } = useDatabaseContent();
  const { formatMessage } = useIntl();
  const dispatch = defaultStoreUtils.useDispatch();
  const store = defaultStoreUtils.useStore();
  const analyticsClient = useAnalytics();
  const { view } = useView();

  const isBrokenDatabase = (
    database: Database | BrokenDatabase | undefined
  ): database is BrokenDatabase => {
    if (database) {
      return 'error' in database;
    } else {
      return false;
    }
  };

  useEffect(() => {
    setDatabaseError(isBrokenDatabase(selectedDatabase));
    if (selectedDatabase) {
      currentBridgeData.data.contentId = selectedDatabase.contentId;
    }
  }, [selectedDatabase]);

  useLayoutEffect(() => {
    globalThis.parent.postMessage(
      JSON.stringify({ databaseReferencingComponentLoaded: true }),
      globalThis.location.origin
    );
  }, []);

  GlobalPageLoadExperience.success({ force: true });

  const baseUrl = getBaseUrl();

  const handleSubmit = useCallback(async () => {
    if (selectedDatabase) {
      setEmbedLoading(true);
      const url = await getEmbedUrl(
        selectedDatabase,
        selectedEntry,
        selectedField,
        dispatch,
        store,
        view,
        analyticsClient,
        spaceKey!,
        baseUrl
      );
      globalThis.parent.postMessage(JSON.stringify(url), globalThis.location.origin);
      setEmbedLoading(false);
    } else {
      setIsEmptySubmit(true);
    }
  }, [
    selectedDatabase,
    selectedEntry,
    selectedField,
    dispatch,
    store,
    view,
    analyticsClient,
    spaceKey,
    baseUrl,
  ]);

  return (
    <Stack
      space="space.200"
      xcss={xcss({
        padding: 'space.100',
      })}
      testId="embed-reference-body"
    >
      <EmbedReferencePicker
        setSelectedDatabase={setSelectedDatabase}
        selectedDatabase={selectedDatabase}
        setSelectedEntry={setSelectedEntry}
        selectedEntry={selectedEntry}
        setSelectedField={setSelectedField}
        selectedField={selectedField}
        databaseError={databaseError}
        isEmptySubmit={isEmptySubmit}
        setIsEmptySubmit={setIsEmptySubmit}
      />

      <DatabasePreview
        databaseError={databaseError}
        selectedDatabase={selectedDatabase}
        selectedEntry={selectedEntry}
        selectedField={selectedField?.id}
        DatabasePreviewComponent={DatabasePreviewComponent}
        EntryPreviewComponent={EntryPreviewComponent}
        ValuePreviewComponent={ValuePreviewComponent}
        ToolbarComponent={ToolbarComponent}
      />
      <Inline
        alignInline="end"
        space="space.100"
        xcss={xcss({
          paddingTop: 'space.100',
          paddingBottom: 'space.200',
        })}
      >
        <Button
          appearance={'subtle'}
          onClick={cancelDatabaseReference}
          testId={'reference-cancel-button'}
        >
          {formatMessage(messages['reference-picker.cancel'])}
        </Button>
        <Button
          testId="reference-submit-button"
          type="submit"
          appearance={'primary'}
          isLoading={embedLoading}
          onClick={handleSubmit}
          isDisabled={!selectedDatabase || databaseError}
        >
          {formatMessage(messages['reference-picker.embed'])}
        </Button>
      </Inline>
    </Stack>
  );
};
export default EmbedReference;
